// The clusters a heatmap draws, and the mask the answers behind them are computed against.
//
// A cluster is authored on the survey and reported on by the answer: the survey asset says where it
// is and what it is called, the answer says what fell inside it. They are paired here, and which of
// them are switched off is what the stored mask names.

import { getSurveyAssets } from '@aytm/app/stats/lib/stat-page'
import { HM_CLUSTER } from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'
import { clusterPosition } from '@aytm/app/stats/page/question-views/hm/lib/props/variants'

// Which clusters a grouping shows. `all` shows every one; `selected` follows what the researcher
// switched off above; the rest each name a way a cluster was arrived at, and a word grouping also
// drops the clusters made of a stop word the researcher is hiding.
export const isClusterHidden = (cluster, blockType, stopWordLevel = []) => {
  if ('all' === blockType) return false
  if ('selected' === blockType) return Boolean(cluster.off)
  if ('editor' === blockType) return !cluster.editor
  if ('custom' === blockType) return 'custom' !== cluster.clusterType

  const attributes = cluster.attributes

  if ('word' === blockType) {
    const isWord = 'word' === attributes?.blockType

    if (!stopWordLevel.length) return !isWord

    return !(isWord && null != attributes?.stopWordLevel && !stopWordLevel.includes(String(attributes.stopWordLevel)))
  }

  return blockType !== attributes?.blockType
}

// Whether the question was drawn with any clusters at all. The survey says so rather than the
// answer, so it reads the same while the answers are away.
export const hasClusters = visualPosition =>
  getSurveyAssets(HM_CLUSTER).some(asset => asset.qid === `q${visualPosition}`)

// The clusters this question was drawn with, each carrying what the answer reported for it. A mask
// the researcher stored names the ones still switched on.
export const calcClusters = ({ visualPosition, answer, config } = {}) => {
  const stored = config?.clusters
  const reported = answer?.heatmap?.clusters || []

  return getSurveyAssets(HM_CLUSTER)
    .filter(asset => asset.qid === `q${visualPosition}`)
    .map(asset => {
      const stat = reported.find(cluster => cluster.uid === asset.uid)

      const cluster = {
        border: asset.border || [],
        cloudWords: stat?.cloudWords || {},
        commentTexts: stat?.commentTexts || [],
        color: asset.color,
        clusterType: asset.clusterType,
        off: null != stored && !stored.includes(`${asset.uid}`),
        points: stat?.points || [],
        qid: asset.qid,
        title: asset.title,
        uid: asset.uid,
        hash: asset.hash || null,
        stat: {
          comments: stat?.stat?.comments || 0,
          respondents: stat?.stat?.respondents || 0,
          points: stat?.stat?.points || 0
        }
      }

      if (asset.attributes) cluster.attributes = asset.attributes
      if (asset.editor) cluster.editor = asset.editor

      return cluster
    })
}

// The same clusters, with the grouping on screen deciding which of them are switched off.
export const applyHiddenStatus = (clusters, blockType, stopWordLevel) =>
  (clusters || []).map(cluster => ({ ...cluster, off: isClusterHidden(cluster, blockType, stopWordLevel) }))

// The mask the answers are computed against: silence while every cluster is on, and the list of the
// ones still on otherwise. A question with no clusters at all has nothing to mask to.
export const calcClustersInConfig = (clusters, config = {}) => {
  const next = { ...config }

  const on = (clusters || []).filter(cluster => !cluster.off).map(cluster => cluster.uid)

  if (!clusters?.length && Object.hasOwn(next, 'applyClustersMask')) next.applyClustersMask = false

  if (clusters?.length === on.length) delete next.clusters
  else next.clusters = on

  return next
}

const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// A cluster's row keeps its own report params: its word cloud, keyed `wc<question>-<cluster>`, and
// every section the row writes, keyed `<prefix><question>.<cluster>` -- its view mode among them. A
// cluster that is gone leaves all of them behind naming nothing, so they are cleared with it.
export const calcStaleClusterParams = (params, questionKey, clusters) => {
  const key = escapeRegExp(questionKey)
  const cloud = new RegExp(`^wc${key}-(\\S+a_aytm)$`, 'i')
  const row = new RegExp(`^[a-z]+${key}\\.(\\S+a_aytm)$`, 'i')

  const live = (clusters || []).map(cluster => clusterPosition(cluster.uid))

  return Object.keys(params || {})
    .filter(name => {
      const position = (cloud.exec(name) || row.exec(name))?.[1]
      return null != position && !live.includes(position)
    })
    .reduce((acc, name) => Object.assign(acc, { [name]: null }), {})
}
